import { useQuery } from '@tanstack/react-query';
import { $api, ApiError, unwrap } from '~/lib/api-client';
import React from 'react';

import { match } from 'ts-pattern';

import { PageLoader } from 'Router/Routes';
import MechanicRedirectModal from 'Components/MechanicRedirectModal/MechanicRedirectModal';
import { logout } from 'Utils/auth';
import { Navigate, useNavigate } from 'react-router-dom';

import env from '../Assets/env.json';

const CLIENT_LIGHT_ROLE_NAME = 'Client light';
const MECHANIC_ROLE_NAME = 'Monteur';
const ADMIN_ROLE_NAME = 'Admin';

const userFetchFn = async () => {
  const user = await unwrap($api['/liftstatus/user'].GET());

  const isClientLight = user.roles.some(
    (role) => role.name === CLIENT_LIGHT_ROLE_NAME
  );

  const isMechanic = user.roles.some(
    (role) => role.name === MECHANIC_ROLE_NAME
  );

  const isAdmin =
    user.roles.some((role) => role.name === ADMIN_ROLE_NAME) ?? false;

  return { ...user, isClientLight, isMechanic, isAdmin };
};

type User = Awaited<ReturnType<typeof userFetchFn>>;

const UserContext = React.createContext<User | null>(null);

const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();

  // The cookie is HttpOnly, so the presence of a token says nothing.
  // Always ask the API; a 401 or 404 means "not logged in".
  const userQuery = useQuery({
    queryKey: ['liftstatus', 'user'],
    queryFn: userFetchFn,
    retry: false,
  });

  return match(userQuery)
    .with({ status: 'pending' }, () => <PageLoader />)
    .with({ status: 'error' }, ({ error }) => {
      if (error instanceof ApiError && [401, 404].includes(error.status)) {
        logout();
        return <Navigate to="/login" />;
      }
      return <p>{error.message}</p>;
    })
    .with({ status: 'success' }, ({ data: user }) => (
      <UserContext.Provider value={user}>
        {children}

        {user.isMechanic && (
          <MechanicRedirectModal
            onRedirect={() => {
              logout();

              window.location.replace(
                env.endpoint.includes('-tst')
                  ? 'https://ednl-mechanic-app-test.pages.dev'
                  : 'https://app.liftstatus.nl/'
              );
            }}
            onUseDifferentAccount={() => {
              logout();
              navigate('/login', { replace: true });
            }}
          />
        )}
      </UserContext.Provider>
    ))
    .exhaustive();
};

const useUser = () => {
  const context = React.useContext(UserContext);

  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }

  return context;
};

export { UserProvider, useUser };
